import React, { useMemo, useState, useEffect, useRef, useCallback } from 'react';
import {
    Card,
    CardHeader,
    CardBody,
    Text,
    Heading,
    Box,
    Stack,
    StackDivider,
    Slider,
    SliderTrack,
    SliderThumb,
    SliderFilledTrack,
    SliderMark,
    Tooltip,
    NumberInputField,
    NumberInput,
    NumberInputStepper,
    NumberIncrementStepper,
    NumberDecrementStepper,
    Table,
    TableContainer,
    Thead,
    Tr,
    Th,
    Td,
    Tbody,
    SimpleGrid,
    GridItem,
    Flex,
    useColorModeValue,
    Radio,
    RadioGroup,
    Checkbox,
    VStack,
    Badge,
    FormControl,
    FormLabel,
    Switch,
    Button,
    useDisclosure
} from '@chakra-ui/react'
import { InfoIcon, DownloadIcon } from '@chakra-ui/icons';
import ConfigView from './ConfigView';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { logToSplunk } from '../utils/splunkLogger';
import ExportPdfModal from './ExportPdfModal';

function Body() {
    const { isOpen, onOpen, onClose } = useDisclosure();
    const inactivityTimer = useRef(null);

    // Inactivity Tracker
    const resetInactivityTimer = useCallback(() => {
        if (inactivityTimer.current) clearTimeout(inactivityTimer.current);

        inactivityTimer.current = setTimeout(() => {
            // User is inactive for 10s
            const currentUrl = window.location.href;
            logToSplunk({
                type: 'inactivity_10s',
                url: currentUrl
            });
        }, 10000);
    }, []);

    useEffect(() => {
        const events = ['mousemove', 'keydown', 'click', 'scroll', 'touchstart'];
        const handleActivity = () => resetInactivityTimer();

        events.forEach(event => window.addEventListener(event, handleActivity));

        // Start timer on mount
        resetInactivityTimer();

        return () => {
            events.forEach(event => window.removeEventListener(event, handleActivity));
            if (inactivityTimer.current) clearTimeout(inactivityTimer.current);
        };
    }, [resetInactivityTimer]);

    // PDF Export
    const handleExportPdf = async (email) => {
        // Close modal first
        onClose();

        // Log to Splunk first
        const currentUrl = window.location.href;
        await logToSplunk({
            type: 'pdf_export',
            url: currentUrl,
            email: email
        });

        // Wait for modal to close (animation)
        setTimeout(async () => {
            // Generate PDF
            const element = document.documentElement; // Capture the whole page

            try {
                const canvas = await html2canvas(element, {
                    scale: 2,
                    useCORS: true,
                    logging: false,
                    windowHeight: element.scrollHeight
                });

                const imgData = canvas.toDataURL('image/png');
                const pdf = new jsPDF('p', 'mm', 'a4');
                const pdfWidth = pdf.internal.pageSize.getWidth();
                const pdfHeight = pdf.internal.pageSize.getHeight();

                const imgProps = pdf.getImageProperties(imgData);
                const imgHeight = (imgProps.height * pdfWidth) / imgProps.width;

                let heightLeft = imgHeight;
                let position = 0;

                pdf.addImage(imgData, 'PNG', 0, position, pdfWidth, imgHeight);
                heightLeft -= pdfHeight;

                while (heightLeft >= 0) {
                    position = heightLeft - imgHeight;
                    pdf.addPage();
                    pdf.addImage(imgData, 'PNG', 0, position, pdfWidth, imgHeight);
                    heightLeft -= pdfHeight;
                }

                // Manual save to ensure filename
                const pdfOutput = pdf.output('blob');
                const url = URL.createObjectURL(pdfOutput);
                const link = document.createElement('a');
                link.href = url;
                link.download = 'splunk-sizing-report.pdf';
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                URL.revokeObjectURL(url);

            } catch (error) {
                console.error("PDF Generation failed", error);
                alert("Error generating PDF. Please try again.");
            }
        }, 500); // 500ms delay for modal close animation
    };

    // Global Settings
    const [deploymentType, setDeploymentType] = useState('onprem'); // 'onprem' | 'cloud'
    const [architectureType, setArchitectureType] = useState('cluster'); // 'standalone' | 'cluster'

    // Multi-site
    const [isMultiSite, setIsMultiSite] = useState(false);
    const [numSites, setNumSites] = useState(2);
    const [multiSiteHA, setMultiSiteHA] = useState({
        search: false,
        data: false,
        hf: false
    });

    // Input Data
    const [calcMethod, setCalcMethod] = useState('volume'); // 'volume' | 'events'
    const [manualDailyVolume, setManualDailyVolume] = useState(15);
    const [eventSize, setEventSize] = useState(500); // bytes
    const [eps, setEps] = useState(1000); // Events Per Second

    const [rawCompressionFactor, setRawCompressionFactor] = useState(0.15); // Default 0.15
    const [metadataSizeFactor, setMetadataSizeFactor] = useState(0.35); // Default 0.35
    const [showRCFTooltip, setShowRCFTooltip] = useState(false);
    const [showMSFTooltip, setShowMSFTooltip] = useState(false);

    // Premium Apps
    const [premiumApps, setPremiumApps] = useState({
        es: false,
        itsi: false,
        soar: false
    });

    // High Availability (Infrastructure)
    const [infraHA, setInfraHA] = useState({
        search: false,
        clusterMaster: false,
        deploymentServer: false
    });

    // Universal Forwarders (for Cloud/DS sizing)
    const [ufCounts, setUfCounts] = useState({
        windows: 0,
        linux: 0,
        macos: 0
    });

    // Data Retention
    const [useSmartStore, setUseSmartStore] = useState(false);
    const [retentionHotWarm, setRetentionHotWarm] = useState(90); // Default 90
    const [retentionCold, setRetentionCold] = useState(30);
    const [retentionFrozen, setRetentionFrozen] = useState(365);

    // Architecture
    const [numIndexers, setNumIndexers] = useState(5);
    const [replicationFactor, setReplicationFactor] = useState(2); // Default 2
    const [searchFactor, setSearchFactor] = useState(2); // Default 2

    // URL State Sync
    const [isLoaded, setIsLoaded] = useState(false);

    useEffect(() => {
        const params = new URLSearchParams(window.location.search);

        if (params.has('deployment')) setDeploymentType(params.get('deployment'));
        if (params.has('arch')) setArchitectureType(params.get('arch'));
        if (params.has('multisite')) setIsMultiSite(params.get('multisite') === 'true');
        if (params.has('sites')) setNumSites(Number(params.get('sites')));

        if (params.has('ms_ha_search') || params.has('ms_ha_data') || params.has('ms_ha_hf')) {
            setMultiSiteHA({
                search: params.get('ms_ha_search') === 'true',
                data: params.get('ms_ha_data') === 'true',
                hf: params.get('ms_ha_hf') === 'true'
            });
        }

        if (params.has('calc')) setCalcMethod(params.get('calc'));
        if (params.has('vol')) setManualDailyVolume(Number(params.get('vol')));
        if (params.has('evt_size')) setEventSize(Number(params.get('evt_size')));
        if (params.has('eps')) setEps(Number(params.get('eps')));

        if (params.has('comp')) setRawCompressionFactor(Number(params.get('comp')));
        if (params.has('meta')) setMetadataSizeFactor(Number(params.get('meta')));

        if (params.has('es') || params.has('itsi') || params.has('soar')) {
            setPremiumApps({
                es: params.get('es') === 'true',
                itsi: params.get('itsi') === 'true',
                soar: params.get('soar') === 'true'
            });
        }

        if (params.has('ha_search') || params.has('ha_cm') || params.has('ha_ds')) {
            setInfraHA({
                search: params.get('ha_search') === 'true',
                clusterMaster: params.get('ha_cm') === 'true',
                deploymentServer: params.get('ha_ds') === 'true'
            });
        }

        if (params.has('uf_win') || params.has('uf_lin') || params.has('uf_mac')) {
            setUfCounts({
                windows: Number(params.get('uf_win') || 0),
                linux: Number(params.get('uf_lin') || 0),
                macos: Number(params.get('uf_mac') || 0)
            });
        }

        if (params.has('smartstore')) setUseSmartStore(params.get('smartstore') === 'true');
        if (params.has('ret_hw')) setRetentionHotWarm(Number(params.get('ret_hw')));
        if (params.has('ret_cold')) setRetentionCold(Number(params.get('ret_cold')));
        if (params.has('ret_frozen')) setRetentionFrozen(Number(params.get('ret_frozen')));

        if (params.has('idx')) setNumIndexers(Number(params.get('idx')));
        if (params.has('rf')) setReplicationFactor(Number(params.get('rf')));
        if (params.has('sf')) setSearchFactor(Number(params.get('sf')));

        setIsLoaded(true);
    }, []);

    // Update URL on state change
    useEffect(() => {
        if (!isLoaded) return;

        const params = new URLSearchParams();

        params.set('deployment', deploymentType);
        params.set('arch', architectureType);
        params.set('multisite', isMultiSite);
        params.set('sites', numSites);

        params.set('ms_ha_search', multiSiteHA.search);
        params.set('ms_ha_data', multiSiteHA.data);
        params.set('ms_ha_hf', multiSiteHA.hf);

        params.set('calc', calcMethod);
        params.set('vol', manualDailyVolume);
        params.set('evt_size', eventSize);
        params.set('eps', eps);

        params.set('comp', rawCompressionFactor);
        params.set('meta', metadataSizeFactor);

        params.set('es', premiumApps.es);
        params.set('itsi', premiumApps.itsi);
        params.set('soar', premiumApps.soar);

        params.set('ha_search', infraHA.search);
        params.set('ha_cm', infraHA.clusterMaster);
        params.set('ha_ds', infraHA.deploymentServer);

        params.set('uf_win', ufCounts.windows);
        params.set('uf_lin', ufCounts.linux);
        params.set('uf_mac', ufCounts.macos);

        params.set('smartstore', useSmartStore);
        params.set('ret_hw', retentionHotWarm);
        params.set('ret_cold', retentionCold);
        params.set('ret_frozen', retentionFrozen);

        params.set('idx', numIndexers);
        params.set('rf', replicationFactor);
        params.set('sf', searchFactor);

        const newUrl = `${window.location.pathname}?${params.toString()}`;
        window.history.replaceState({}, '', newUrl);

    }, [isLoaded, deploymentType, architectureType, isMultiSite, numSites, multiSiteHA, calcMethod, manualDailyVolume, eventSize, eps, rawCompressionFactor, metadataSizeFactor, premiumApps, infraHA, ufCounts, useSmartStore, retentionHotWarm, retentionCold, retentionFrozen, numIndexers, replicationFactor, searchFactor]);

    // Derived Daily Data Volume
    const dailyDataVolume = useMemo(() => {
        if (calcMethod === 'volume') {
            return manualDailyVolume;
        } else {
            // (Event Size (bytes) * EPS * 86400) / (1024^3) -> GB
            const dailyEvents = eps * 86400;
            const gb = (eventSize * dailyEvents) / (1024 * 1024 * 1024);
            return parseFloat(gb.toFixed(2)); // Round to 2 decimal places
        }
    }, [calcMethod, manualDailyVolume, eventSize, eps]);

    // Automatic Indexer Sizing Effect
    useEffect(() => {
        const hasPremiumApp = premiumApps.es || premiumApps.itsi || premiumApps.soar;
        const gbPerIndexer = hasPremiumApp ? 180 : 300;
        let suggested = Math.ceil(dailyDataVolume / gbPerIndexer);

        // Multi-site HA Logic: Min 1 per site if Data HA is enabled
        if (isMultiSite && multiSiteHA.data) {
            suggested = Math.max(suggested, numSites);
        }

        setNumIndexers(Math.max(1, suggested));
    }, [dailyDataVolume, premiumApps, isMultiSite, multiSiteHA.data, numSites]);

    // Ensure Architecture is Cluster if Cloud
    useEffect(() => {
        if (deploymentType === 'cloud') {
            setArchitectureType('cluster');
        }
    }, [deploymentType]);

    // Calculations
    const results = useMemo(() => {
        // Effective Factors (Force 1 for Standalone)
        const effRF = architectureType === 'standalone' ? 1 : replicationFactor;
        const effSF = architectureType === 'standalone' ? 1 : searchFactor;

        // Effective Retention (Zero out Cold/Frozen if SmartStore is enabled)
        const effRetCold = useSmartStore ? 0 : retentionCold;
        const effRetFrozen = useSmartStore ? 0 : retentionFrozen;

        // Storage Calculations (GB)
        const resultHW_GB = Math.round((dailyDataVolume * rawCompressionFactor * effRF * retentionHotWarm) + (dailyDataVolume * metadataSizeFactor * effSF * retentionHotWarm));
        const resultHWPerIdx_GB = Math.round(resultHW_GB / numIndexers);

        const resultCO_GB = Math.round((dailyDataVolume * rawCompressionFactor * effRF * effRetCold) + (dailyDataVolume * metadataSizeFactor * effSF * effRetCold));
        const resultCOPerIdx_GB = Math.round(resultCO_GB / numIndexers);

        const resultFR_GB = Math.round((dailyDataVolume * rawCompressionFactor * effRF * effRetFrozen));
        const resultFRPerIdx_GB = Math.round(resultFR_GB / numIndexers);

        const resultTotal_GB = resultHW_GB + resultCO_GB + resultFR_GB;
        const resultTotalPerIdx_GB = resultHWPerIdx_GB + resultCOPerIdx_GB + resultFRPerIdx_GB;

        // Convert to TB for Display
        const toTB = (gb) => (gb / 1024).toFixed(2);

        // Indexer Sizing Logic (for reference/badge)
        const hasPremiumApp = premiumApps.es || premiumApps.itsi || premiumApps.soar;
        const gbPerIndexer = hasPremiumApp ? 180 : 300;
        let suggestedIndexers = Math.ceil(dailyDataVolume / gbPerIndexer);
        // Multi-site HA Logic: Min 1 per site if Data HA is enabled
        if (isMultiSite && multiSiteHA.data) {
            suggestedIndexers = Math.max(suggestedIndexers, numSites);
        }

        // Cloud / Forwarder Sizing Logic
        // Assumption: 1 HF per 200GB/day (parsing/routing)
        let suggestedHFs = Math.ceil(dailyDataVolume / 200);
        // Multi-site HA Logic: Min 1 per site if HF HA is enabled
        if (isMultiSite && multiSiteHA.hf) {
            suggestedHFs = Math.max(suggestedHFs, numSites);
        }

        // DS Sizing: 1 DS per 8000 UFs
        const totalUFs = ufCounts.windows + ufCounts.linux + ufCounts.macos;
        let suggestedDS = Math.ceil(totalUFs / 8000);

        // Multi-site DS Logic: 1 DS per Site
        if (isMultiSite) {
            suggestedDS = Math.max(suggestedDS, numSites);
        }
        // HA Logic for DS: Double the count if HA is enabled
        if (infraHA.deploymentServer) {
            suggestedDS = suggestedDS * 2;
        }
        // Ensure at least 0 if no UFs, but if UFs > 0, min 1 (or 2 if HA)
        if (totalUFs > 0 && suggestedDS === 0) suggestedDS = 1;


        // Search Head Sizing
        // Rule: SHC is required only if daily volume > 200GB OR Search Head HA is enabled
        // Each Premium App gets its own dedicated SHC with minimum 3 members.
        let searchHeadClusters = [];

        const requiresSHC = (architectureType === 'cluster' && (dailyDataVolume > 200 || infraHA.search));

        if (requiresSHC) {
            // Add Core SHC (always present when SHC is required)
            searchHeadClusters.push({ label: 'SHC Core Members', count: 3 });

            // Add Premium App SHCs
            if (premiumApps.es) {
                searchHeadClusters.push({ label: 'SHC ES Members', count: 3 });
            }
            if (premiumApps.itsi) {
                searchHeadClusters.push({ label: 'SHC ITSI Members', count: 3 });
            }
            if (premiumApps.soar) {
                searchHeadClusters.push({ label: 'SHC SOAR Members', count: 3 });
            }
        } else {
            // Single Search Heads (no cluster)
            searchHeadClusters.push({ label: 'Search Heads (SH)', count: 1 });

            // Add separate SH for each premium app
            if (premiumApps.es) {
                searchHeadClusters.push({ label: 'Search Heads ES (SH)', count: 1 });
            }
            if (premiumApps.itsi) {
                searchHeadClusters.push({ label: 'Search Heads ITSI (SH)', count: 1 });
            }
            if (premiumApps.soar) {
                searchHeadClusters.push({ label: 'Search Heads SOAR (SH)', count: 1 });
            }
        }

        // SOAR Logic
        const soarServerCount = premiumApps.soar ? 1 : 0;

        // Cluster Master Logic
        let clusterMasterCount = 0;
        if (architectureType === 'cluster' && deploymentType === 'onprem') {
            clusterMasterCount = infraHA.clusterMaster ? 2 : 1;
        }

        // Automation Broker Logic
        // 1 per site if SOAR is enabled
        let automationBrokerCount = 0;
        if (premiumApps.soar) {
            automationBrokerCount = isMultiSite ? numSites : 1;
        }

        // SHC Deployer Logic
        // Each SHC needs one deployer
        // Recommended specs: 12 vCPU, 12GB RAM, 200GB disk
        let shcDeployerCount = 0;
        if (requiresSHC) {
            // Count how many SHCs we have
            shcDeployerCount = searchHeadClusters.length;
        }

        return {
            hw: toTB(resultHW_GB),
            hwPerIdx: toTB(resultHWPerIdx_GB),
            co: toTB(resultCO_GB),
            coPerIdx: toTB(resultCOPerIdx_GB),
            fr: toTB(resultFR_GB),
            frPerIdx: toTB(resultFRPerIdx_GB),
            total: toTB(resultTotal_GB),
            totalPerIdx: toTB(resultTotalPerIdx_GB),
            // Keep raw GB for config generation
            raw: {
                hwPerIdx: resultHWPerIdx_GB,
                coPerIdx: resultCOPerIdx_GB
            },
            suggestedIndexers,
            suggestedHFs,
            suggestedDS,
            totalUFs,
            effRF,
            effSF,
            searchHeadClusters,
            clusterMasterCount,
            soarServerCount,
            automationBrokerCount,
            shcDeployerCount
        };
    }, [dailyDataVolume, rawCompressionFactor, metadataSizeFactor, retentionHotWarm, retentionCold, retentionFrozen, numIndexers, replicationFactor, searchFactor, premiumApps, ufCounts, architectureType, isMultiSite, numSites, infraHA, multiSiteHA, useSmartStore, deploymentType]);

    const configCode = useMemo(() => {
        const repFactorLine = architectureType === 'cluster' ? '\nrepFactor = auto' : '';

        if (useSmartStore) {
            return `[volume:primary]
path = /mnt/fast_disk
maxVolumeDataSizeMB = ${results.raw.hwPerIdx * 1024}

[volume:remote_store]
storageType = remote
path = s3://my-bucket
remote.s3.access_key = <your_access_key>
remote.s3.secret_key = <your_secret_key>
remote.s3.bucket_name = my-bucket

[default]
remotePath = volume:remote_store

[example_index]
homePath = volume:primary/example_index/db
coldPath = volume:primary/example_index/colddb
thawedPath = $SPLUNK_DB/example_index/thaweddb${repFactorLine}`;
        } else {
            return `[volume:hot_examples]
path = /mnt/fast_disk
maxVolumeDataSizeMB = ${results.raw.hwPerIdx * 1024}

[volume:cold_examples]
path = /mnt/big_disk
maxVolumeDataSizeMB = ${results.raw.coPerIdx * 1024}

[example_index]
homePath = volume:hot_examples/example_index/db
coldPath = volume:cold_examples/example_index/colddb
thawedPath = $SPLUNK_DB/example_index/thaweddb${repFactorLine}`;
        }
    }, [results, useSmartStore, architectureType]);

    const cardBg = useColorModeValue('white', 'gray.800');

    return (
        <Stack spacing={8}>
            <Flex justify="flex-end">
                <Button
                    leftIcon={<DownloadIcon />}
                    colorScheme="red"
                    variant="solid"
                    onClick={onOpen}
                    size="sm"
                    boxShadow="md"
                >
                    Export PDF
                </Button>
            </Flex>

            <ExportPdfModal
                isOpen={isOpen}
                onClose={onClose}
                onDownload={handleExportPdf}
            />

            {/* Top Controls: Deployment Type */}
            <Card bg={cardBg}>
                <CardBody>
                    <Flex align="center" justify="space-between" wrap="wrap" gap={4}>
                        <Box>
                            <Heading size='sm' textTransform='uppercase' mb={2}>Deployment Type</Heading>
                            <RadioGroup onChange={setDeploymentType} value={deploymentType}>
                                <Stack direction='row' spacing={5}>
                                    <Radio value='onprem' colorScheme='brand'>Splunk Enterprise (On-Prem)</Radio>
                                    <Radio value='cloud' colorScheme='brand'>Splunk Cloud</Radio>
                                </Stack>
                            </RadioGroup>
                        </Box>

                        {deploymentType === 'onprem' && (
                            <Box>
                                <Heading size='sm' textTransform='uppercase' mb={2}>Architecture</Heading>
                                <RadioGroup onChange={setArchitectureType} value={architectureType}>
                                    <Stack direction='row' spacing={5}>
                                        <Radio value='standalone' colorScheme='brand'>Standalone</Radio>
                                        <Radio value='cluster' colorScheme='brand'>Cluster</Radio>
                                    </Stack>
                                </RadioGroup>
                            </Box>
                        )}

                        <Box>
                            <Heading size='sm' textTransform='uppercase' mb={2}>Premium Apps</Heading>
                            <Stack direction='row' spacing={4}>
                                <Checkbox
                                    isChecked={premiumApps.es}
                                    onChange={(e) => setPremiumApps({ ...premiumApps, es: e.target.checked })}
                                    colorScheme='brand'
                                >Splunk ES</Checkbox>
                                <Checkbox
                                    isChecked={premiumApps.itsi}
                                    onChange={(e) => setPremiumApps({ ...premiumApps, itsi: e.target.checked })}
                                    colorScheme='brand'
                                >ITSI</Checkbox>
                                <Checkbox
                                    isChecked={premiumApps.soar}
                                    onChange={(e) => setPremiumApps({ ...premiumApps, soar: e.target.checked })}
                                    colorScheme='brand'
                                >SOAR</Checkbox>
                            </Stack>
                        </Box>
                    </Flex>
                </CardBody>
            </Card>

            <SimpleGrid columns={{ base: 1, lg: 3 }} spacing={8}>
                {/* Input Column */}
                <GridItem colSpan={{ base: 1, lg: 2 }}>
                    <Stack spacing={8}>
                        {/* Input Data Card */}
                        <Card bg={cardBg}>
                            <CardHeader>
                                <Heading size='md'>Input Data</Heading>
                                <Text fontSize='sm' color="gray.500">Set input data information.</Text>
                            </CardHeader>
                            <CardBody>
                                <Stack divider={<StackDivider />} spacing='6'>
                                    <Box>
                                        <Flex justify="space-between" align="center" mb={4}>
                                            <Heading size='xs' textTransform='uppercase'>Calculation Method</Heading>
                                            <RadioGroup onChange={setCalcMethod} value={calcMethod} size="sm">
                                                <Stack direction='row' spacing={4}>
                                                    <Radio value='volume' colorScheme='brand'>Daily Volume</Radio>
                                                    <Radio value='events' colorScheme='brand'>EPS (Events Per Second)</Radio>
                                                </Stack>
                                            </RadioGroup>
                                        </Flex>

                                        {calcMethod === 'volume' ? (
                                            <Box>
                                                <Heading size='xs' textTransform='uppercase' mb={2}>Daily Data Volume (GB)</Heading>
                                                <NumberInput
                                                    value={manualDailyVolume}
                                                    onChange={(val) => setManualDailyVolume(Number(val))}
                                                    min={1} max={100000}
                                                >
                                                    <NumberInputField />
                                                    <NumberInputStepper>
                                                        <NumberIncrementStepper />
                                                        <NumberDecrementStepper />
                                                    </NumberInputStepper>
                                                </NumberInput>
                                            </Box>
                                        ) : (
                                            <Stack spacing={4}>
                                                <Box>
                                                    <Heading size='xs' textTransform='uppercase' mb={2}>Avg Event Size (Bytes)</Heading>
                                                    <NumberInput
                                                        value={eventSize}
                                                        onChange={(val) => setEventSize(Number(val))}
                                                        min={1}
                                                    >
                                                        <NumberInputField />
                                                        <NumberInputStepper><NumberIncrementStepper /><NumberDecrementStepper /></NumberInputStepper>
                                                    </NumberInput>
                                                </Box>
                                                <Box>
                                                    <Heading size='xs' textTransform='uppercase' mb={2}>EPS (Events/Sec)</Heading>
                                                    <NumberInput
                                                        value={eps}
                                                        onChange={(val) => setEps(Number(val))}
                                                        min={1}
                                                    >
                                                        <NumberInputField />
                                                        <NumberInputStepper><NumberIncrementStepper /><NumberDecrementStepper /></NumberInputStepper>
                                                    </NumberInput>
                                                </Box>
                                                <Box bg="brand.50" p={3} borderRadius="md" _dark={{ bg: 'whiteAlpha.100' }}>
                                                    <Text fontSize="sm" fontWeight="bold">Calculated Daily Volume: {dailyDataVolume} GB</Text>
                                                </Box>
                                            </Stack>
                                        )}
                                    </Box>

                                    {/* Universal Forwarders Input */}
                                    <Box>
                                        <Heading size='xs' textTransform='uppercase' mb={4}>Universal Forwarders (Endpoints)</Heading>
                                        <SimpleGrid columns={3} spacing={4}>
                                            <Box>
                                                <Text fontSize='xs' mb={1}>Windows</Text>
                                                <NumberInput value={ufCounts.windows} onChange={(val) => setUfCounts({ ...ufCounts, windows: Number(val) })} min={0}>
                                                    <NumberInputField />
                                                </NumberInput>
                                            </Box>
                                            <Box>
                                                <Text fontSize='xs' mb={1}>Linux</Text>
                                                <NumberInput value={ufCounts.linux} onChange={(val) => setUfCounts({ ...ufCounts, linux: Number(val) })} min={0}>
                                                    <NumberInputField />
                                                </NumberInput>
                                            </Box>
                                            <Box>
                                                <Text fontSize='xs' mb={1}>MacOS</Text>
                                                <NumberInput value={ufCounts.macos} onChange={(val) => setUfCounts({ ...ufCounts, macos: Number(val) })} min={0}>
                                                    <NumberInputField />
                                                </NumberInput>
                                            </Box>
                                        </SimpleGrid>
                                    </Box>

                                    {/* Advanced Settings (Compression) - On-Prem Only */}
                                    {deploymentType === 'onprem' && (
                                        <>
                                            <Box>
                                                <Flex align="center" mb={2}>
                                                    <Heading size='xs' textTransform='uppercase' mr={2}>Raw Compression Factor</Heading>
                                                    <Tooltip label="Expected raw data compression rate (Default 0.15)">
                                                        <InfoIcon boxSize={3} color="gray.400" />
                                                    </Tooltip>
                                                </Flex>
                                                <Slider
                                                    value={rawCompressionFactor}
                                                    onChange={setRawCompressionFactor}
                                                    onMouseEnter={() => setShowRCFTooltip(true)}
                                                    onMouseLeave={() => setShowRCFTooltip(false)}
                                                    min={0} max={1} step={0.01}
                                                    mb={6}
                                                >
                                                    <SliderMark value={0} mt='2' fontSize='xs'>0</SliderMark>
                                                    <SliderMark value={0.5} mt='2' fontSize='xs'>0.5</SliderMark>
                                                    <SliderMark value={1} mt='2' fontSize='xs'>1</SliderMark>
                                                    <SliderTrack>
                                                        <SliderFilledTrack />
                                                    </SliderTrack>
                                                    <Tooltip hasArrow placement='top' isOpen={showRCFTooltip} label={`${rawCompressionFactor}`}>
                                                        <SliderThumb boxSize={6} />
                                                    </Tooltip>
                                                </Slider>
                                            </Box>
                                            <Box>
                                                <Flex align="center" mb={2}>
                                                    <Heading size='xs' textTransform='uppercase' mr={2}>Metadata Size Factor</Heading>
                                                    <Tooltip label="Expected metadata size relative to raw data (Default 0.35)">
                                                        <InfoIcon boxSize={3} color="gray.400" />
                                                    </Tooltip>
                                                </Flex>
                                                <Slider
                                                    value={metadataSizeFactor}
                                                    onChange={setMetadataSizeFactor}
                                                    onMouseEnter={() => setShowMSFTooltip(true)}
                                                    onMouseLeave={() => setShowMSFTooltip(false)}
                                                    min={0} max={1} step={0.01}
                                                    mb={6}
                                                >
                                                    <SliderMark value={0} mt='2' fontSize='xs'>0</SliderMark>
                                                    <SliderMark value={0.5} mt='2' fontSize='xs'>0.5</SliderMark>
                                                    <SliderMark value={1} mt='2' fontSize='xs'>1</SliderMark>
                                                    <SliderTrack>
                                                        <SliderFilledTrack />
                                                    </SliderTrack>
                                                    <Tooltip hasArrow placement='top' isOpen={showMSFTooltip} label={`${metadataSizeFactor}`}>
                                                        <SliderThumb boxSize={6} />
                                                    </Tooltip>
                                                </Slider>
                                            </Box>
                                        </>
                                    )}
                                </Stack>
                            </CardBody>
                        </Card>

                        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={8}>
                            {/* Data Retention Card - On-Prem Only */}
                            {deploymentType === 'onprem' && (
                                <Card bg={cardBg}>
                                    <CardHeader>
                                        <Flex justify="space-between" align="center">
                                            <Box>
                                                <Heading size='md'>Data Retention</Heading>
                                                <Text fontSize='sm' color="gray.500">Configure retention policies in days.</Text>
                                            </Box>
                                            {/* SmartStore Toggle */}
                                            <FormControl display='flex' alignItems='center' width="auto">
                                                <FormLabel htmlFor='smartstore-switch' mb='0' fontSize="xs" mr={2}>
                                                    SmartStore
                                                </FormLabel>
                                                <Switch id='smartstore-switch' colorScheme="brand" isChecked={useSmartStore} onChange={(e) => setUseSmartStore(e.target.checked)} />
                                            </FormControl>
                                        </Flex>
                                    </CardHeader>
                                    <CardBody>
                                        <Stack spacing='4'>
                                            <Box>
                                                <Heading size='xs' textTransform='uppercase' mb={2}>Hot / Warm</Heading>
                                                <NumberInput value={retentionHotWarm} onChange={(val) => setRetentionHotWarm(Number(val))} min={1} max={3650}>
                                                    <NumberInputField />
                                                    <NumberInputStepper><NumberIncrementStepper /><NumberDecrementStepper /></NumberInputStepper>
                                                </NumberInput>
                                            </Box>

                                            {!useSmartStore && (
                                                <>
                                                    <Box>
                                                        <Heading size='xs' textTransform='uppercase' mb={2}>Cold</Heading>
                                                        <NumberInput value={retentionCold} onChange={(val) => setRetentionCold(Number(val))} min={0} max={3650}>
                                                            <NumberInputField />
                                                            <NumberInputStepper><NumberIncrementStepper /><NumberDecrementStepper /></NumberInputStepper>
                                                        </NumberInput>
                                                    </Box>
                                                    <Box>
                                                        <Heading size='xs' textTransform='uppercase' mb={2}>Archived (Frozen)</Heading>
                                                        <NumberInput value={retentionFrozen} onChange={(val) => setRetentionFrozen(Number(val))} min={0} max={3650}>
                                                            <NumberInputField />
                                                            <NumberInputStepper><NumberIncrementStepper /><NumberDecrementStepper /></NumberInputStepper>
                                                        </NumberInput>
                                                    </Box>
                                                </>
                                            )}
                                        </Stack>
                                    </CardBody>
                                </Card>
                            )}

                            {/* Architecture Card - Visible for both, but filtered */}
                            <Card bg={cardBg}>
                                <CardHeader>
                                    <Heading size='md'>Architecture</Heading>
                                    <Text fontSize='sm' color="gray.500">Cluster configuration.</Text>
                                </CardHeader>
                                <CardBody>
                                    <Stack spacing='4'>
                                        {/* Multi-site Configuration */}
                                        {architectureType === 'cluster' && (
                                            <Box>
                                                <Flex align="center" justify="space-between" mb={2}>
                                                    <Heading size='xs' textTransform='uppercase'>Multi-site</Heading>
                                                    <Checkbox
                                                        isChecked={isMultiSite}
                                                        onChange={(e) => setIsMultiSite(e.target.checked)}
                                                        colorScheme='brand'
                                                    >Enable</Checkbox>
                                                </Flex>
                                                {isMultiSite && (
                                                    <Box pl={4} borderLeft="2px" borderColor="brand.200" mb={4}>
                                                        <Text fontSize='xs' mb={1}>Number of Sites</Text>
                                                        <NumberInput size="sm" value={numSites} onChange={(val) => setNumSites(Number(val))} min={2} max={10} mb={2}>
                                                            <NumberInputField />
                                                            <NumberInputStepper><NumberIncrementStepper /><NumberDecrementStepper /></NumberInputStepper>
                                                        </NumberInput>
                                                        <Text fontSize='xs' fontWeight="bold" mb={1}>Multi-site HA Levels:</Text>
                                                        <VStack align="start" spacing={1}>
                                                            {deploymentType === 'onprem' && (
                                                                <>
                                                                    <Checkbox size="sm" isChecked={multiSiteHA.search} onChange={(e) => setMultiSiteHA({ ...multiSiteHA, search: e.target.checked })}>Search Head</Checkbox>
                                                                    <Checkbox size="sm" isChecked={multiSiteHA.data} onChange={(e) => setMultiSiteHA({ ...multiSiteHA, data: e.target.checked })}>Data (Indexers)</Checkbox>
                                                                </>
                                                            )}
                                                            <Checkbox size="sm" isChecked={multiSiteHA.hf} onChange={(e) => setMultiSiteHA({ ...multiSiteHA, hf: e.target.checked })}>Heavy Forwarders</Checkbox>
                                                        </VStack>
                                                    </Box>
                                                )}
                                            </Box>
                                        )}

                                        {/* Indexers - On-Prem Only */}
                                        {deploymentType === 'onprem' && (
                                            <Box>
                                                <Heading size='xs' textTransform='uppercase' mb={2}>Number Of Indexers</Heading>
                                                <Flex align="center" mb={2}>
                                                    <NumberInput value={numIndexers} onChange={(val) => setNumIndexers(Number(val))} min={1} max={1000} mr={2}>
                                                        <NumberInputField />
                                                        <NumberInputStepper><NumberIncrementStepper /><NumberDecrementStepper /></NumberInputStepper>
                                                    </NumberInput>
                                                    <Badge colorScheme={numIndexers < results.suggestedIndexers ? 'red' : 'green'}>
                                                        Rec: {results.suggestedIndexers}
                                                    </Badge>
                                                </Flex>
                                                <Text fontSize="xs" color="gray.500">
                                                    Based on {premiumApps.es || premiumApps.itsi || premiumApps.soar ? '180GB' : '300GB'}/idx
                                                </Text>
                                            </Box>
                                        )}

                                        {/* RF/SF - On-Prem Cluster Only AND > 1 Indexer */}
                                        {deploymentType === 'onprem' && architectureType === 'cluster' && numIndexers > 1 && (
                                            <>
                                                <Box>
                                                    <Heading size='xs' textTransform='uppercase' mb={2}>Replication Factor</Heading>
                                                    <NumberInput value={replicationFactor} onChange={(val) => setReplicationFactor(Number(val))} min={1} max={numIndexers}>
                                                        <NumberInputField />
                                                        <NumberInputStepper><NumberIncrementStepper /><NumberDecrementStepper /></NumberInputStepper>
                                                    </NumberInput>
                                                </Box>
                                                <Box>
                                                    <Heading size='xs' textTransform='uppercase' mb={2}>Search Factor</Heading>
                                                    <NumberInput value={searchFactor} onChange={(val) => setSearchFactor(Number(val))} min={1} max={replicationFactor}>
                                                        <NumberInputField />
                                                        <NumberInputStepper><NumberIncrementStepper /><NumberDecrementStepper /></NumberInputStepper>
                                                    </NumberInput>
                                                </Box>
                                            </>
                                        )}

                                        {/* Infrastructure HA */}
                                        <Box>
                                            <Heading size='xs' textTransform='uppercase' mb={2}>Infrastructure HA</Heading>
                                            <VStack align="start">
                                                {/* On-Prem: Show all. Cloud: Show none (managed service) unless specific request. */}

                                                {deploymentType === 'onprem' && (
                                                    <>
                                                        <Checkbox
                                                            isChecked={infraHA.search}
                                                            onChange={(e) => setInfraHA({ ...infraHA, search: e.target.checked })}
                                                        >Search Head HA</Checkbox>
                                                        <Checkbox
                                                            isChecked={infraHA.clusterMaster}
                                                            onChange={(e) => setInfraHA({ ...infraHA, clusterMaster: e.target.checked })}
                                                        >Cluster Master HA</Checkbox>
                                                    </>
                                                )}

                                                {/* Deployment Server HA - Show if DS > 0 */}
                                                {results.suggestedDS > 0 && (
                                                    <Checkbox
                                                        isChecked={infraHA.deploymentServer}
                                                        onChange={(e) => setInfraHA({ ...infraHA, deploymentServer: e.target.checked })}
                                                    >Deployment Server HA</Checkbox>
                                                )}
                                            </VStack>
                                        </Box>
                                    </Stack>
                                </CardBody>
                            </Card>
                        </SimpleGrid>
                    </Stack>
                </GridItem>

                {/* Result Column */}
                <GridItem colSpan={{ base: 1, lg: 1 }}>
                    <Box position="sticky" top="20px">
                        <Stack spacing={4}>
                            {/* Infrastructure Sizing (Common) */}
                            <Card bg={cardBg} variant="outline" borderColor="brand.500" borderWidth="2px">
                                <CardHeader bg="brand.500" color="white" borderTopRadius="xl">
                                    <Heading size='md'>Infrastructure Sizing</Heading>
                                </CardHeader>
                                <CardBody>
                                    <TableContainer>
                                        <Table variant='simple' size="sm">
                                            <Tbody>
                                                {/* Standalone Mode: Show "Standalone: 1" and DS if needed */}
                                                {deploymentType === 'onprem' && architectureType === 'standalone' ? (
                                                    <>
                                                        <Tr>
                                                            <Td fontWeight="bold">Standalone Instance</Td>
                                                            <Td isNumeric>1</Td>
                                                        </Tr>
                                                        {results.totalUFs > 100 && (
                                                            <Tr>
                                                                <Td fontWeight="bold">Deployment Servers</Td>
                                                                <Td isNumeric>{results.suggestedDS}</Td>
                                                            </Tr>
                                                        )}
                                                        {/* SOAR On-Prem */}
                                                        {premiumApps.soar && (
                                                            <Tr>
                                                                <Td fontWeight="bold">Splunk SOAR Server</Td>
                                                                <Td isNumeric>{results.soarServerCount}</Td>
                                                            </Tr>
                                                        )}
                                                        {/* Automation Broker (if SOAR selected) */}
                                                        {premiumApps.soar && (
                                                            <Tr>
                                                                <Td fontWeight="bold">Automation Broker</Td>
                                                                <Td isNumeric>{results.automationBrokerCount}</Td>
                                                            </Tr>
                                                        )}
                                                    </>
                                                ) : (
                                                    /* Cluster / Cloud Mode */
                                                    <>
                                                        {/* Heavy Forwarders - Hide if Standalone (handled by conditional above) */}
                                                        <Tr>
                                                            <Td fontWeight="bold">Heavy Forwarders</Td>
                                                            <Td isNumeric>{results.suggestedHFs}</Td>
                                                        </Tr>

                                                        {/* Deployment Servers */}
                                                        <Tr>
                                                            <Td fontWeight="bold">Deployment Servers</Td>
                                                            <Td isNumeric>{results.suggestedDS}</Td>
                                                        </Tr>

                                                        {/* On-Prem Cluster Specifics */}
                                                        {deploymentType === 'onprem' && (
                                                            <>
                                                                {/* Search Head Clusters - Map over array */}
                                                                {results.searchHeadClusters.map((shc, index) => (
                                                                    <Tr key={index}>
                                                                        <Td fontWeight="bold">{shc.label}</Td>
                                                                        <Td isNumeric>{shc.count}</Td>
                                                                    </Tr>
                                                                ))}
                                                                {/* SHC Deployer */}
                                                                {results.shcDeployerCount > 0 && (
                                                                    <Tr>
                                                                        <Td fontWeight="bold">SHC Deployer</Td>
                                                                        <Td isNumeric>{results.shcDeployerCount}</Td>
                                                                    </Tr>
                                                                )}
                                                                <Tr>
                                                                    <Td fontWeight="bold">Indexers</Td>
                                                                    <Td isNumeric>{results.suggestedIndexers}</Td>
                                                                </Tr>
                                                                <Tr>
                                                                    <Td fontWeight="bold">Cluster Master</Td>
                                                                    <Td isNumeric>{results.clusterMasterCount}</Td>
                                                                </Tr>
                                                                {/* SOAR On-Prem */}
                                                                {premiumApps.soar && (
                                                                    <Tr>
                                                                        <Td fontWeight="bold">Splunk SOAR Server</Td>
                                                                        <Td isNumeric>{results.soarServerCount}</Td>
                                                                    </Tr>
                                                                )}
                                                            </>
                                                        )}

                                                        {/* Automation Broker (Common for both if SOAR selected) */}
                                                        {premiumApps.soar && (
                                                            <Tr>
                                                                <Td fontWeight="bold">Automation Broker</Td>
                                                                <Td isNumeric>{results.automationBrokerCount}</Td>
                                                            </Tr>
                                                        )}
                                                    </>
                                                )}
                                            </Tbody>
                                        </Table>
                                    </TableContainer>
                                </CardBody>
                            </Card>

                            {/* Storage Sizing (On-Prem Only) */}
                            {deploymentType === 'onprem' && (
                                <Card bg={cardBg} variant="outline" borderColor="brand.500" borderWidth="2px">
                                    <CardHeader bg="brand.500" color="white" borderTopRadius="xl">
                                        <Heading size='md'>Storage Sizing</Heading>
                                        <Text fontSize='sm' opacity={0.9}>Estimated storage requirements.</Text>
                                    </CardHeader>
                                    <CardBody>
                                        <Stack divider={<StackDivider />} spacing='6'>
                                            <Box>
                                                <TableContainer>
                                                    <Table variant='simple' size="sm">
                                                        <Thead>
                                                            <Tr>
                                                                <Th>Type</Th>
                                                                <Th isNumeric>Total (TB)</Th>
                                                                <Th isNumeric>Per Idx (TB)</Th>
                                                            </Tr>
                                                        </Thead>
                                                        <Tbody>
                                                            <Tr>
                                                                <Td fontWeight="bold">Hot/Warm</Td>
                                                                <Td isNumeric>{results.hw}</Td>
                                                                <Td isNumeric>{results.hwPerIdx}</Td>
                                                            </Tr>
                                                            {!useSmartStore && (
                                                                <>
                                                                    <Tr>
                                                                        <Td fontWeight="bold">Cold</Td>
                                                                        <Td isNumeric>{results.co}</Td>
                                                                        <Td isNumeric>{results.coPerIdx}</Td>
                                                                    </Tr>
                                                                    <Tr>
                                                                        <Td fontWeight="bold">Archived</Td>
                                                                        <Td isNumeric>{results.fr}</Td>
                                                                        <Td isNumeric>{results.frPerIdx}</Td>
                                                                    </Tr>
                                                                </>
                                                            )}
                                                            <Tr bg="brand.50" _dark={{ bg: 'whiteAlpha.100' }}>
                                                                <Td fontWeight="bold">Total</Td>
                                                                <Td isNumeric fontWeight="bold">{results.total}</Td>
                                                                <Td isNumeric fontWeight="bold">{results.totalPerIdx}</Td>
                                                            </Tr>
                                                        </Tbody>
                                                    </Table>
                                                </TableContainer>
                                            </Box>
                                            <Box>
                                                <Heading size='xs' textTransform='uppercase' mb={2}>indexes.conf Example</Heading>
                                                <ConfigView code={configCode} language='ini' />
                                            </Box>
                                        </Stack>
                                    </CardBody>
                                </Card>
                            )}
                        </Stack>
                    </Box>
                </GridItem>
            </SimpleGrid>

            {/* Hardware Resource Summary Table */}
            <Card bg={cardBg} variant="outline" borderColor="gray.200" borderWidth="1px">
                <CardHeader>
                    <Heading size='md'>Hardware Resource Summary</Heading>
                    <Text fontSize='sm' color="gray.500">Recommended minimum specifications based on Splunk Reference Hardware.</Text>
                </CardHeader>
                <CardBody>
                    <TableContainer>
                        <Table variant='simple' size="sm">
                            <Thead>
                                <Tr>
                                    <Th>Role</Th>
                                    <Th isNumeric>Count</Th>
                                    <Th>CPU (vCPU)</Th>
                                    <Th>Memory (RAM)</Th>
                                    <Th>Disk (OS + Data)</Th>
                                </Tr>
                            </Thead>
                            <Tbody>
                                {/* Standalone */}
                                {deploymentType === 'onprem' && architectureType === 'standalone' && (
                                    <Tr>
                                        <Td fontWeight="bold">Standalone Instance</Td>
                                        <Td isNumeric>1</Td>
                                        <Td>16 vCPU</Td>
                                        <Td>32 GB</Td>
                                        <Td>300 GB + {results.total} TB</Td>
                                    </Tr>
                                )}

                                {/* Cluster / Cloud Roles */}
                                {!(deploymentType === 'onprem' && architectureType === 'standalone') && (
                                    <>
                                        {/* Search Heads - On-Prem Only */}
                                        {deploymentType === 'onprem' && results.searchHeadClusters.map((shc, index) => (
                                            <Tr key={index}>
                                                <Td fontWeight="bold">{shc.label}</Td>
                                                <Td isNumeric>{shc.count}</Td>
                                                <Td>16 vCPU</Td>
                                                <Td>32 GB</Td>
                                                <Td>200 GB</Td>
                                            </Tr>
                                        ))}

                                        {/* SHC Deployer - On-Prem Only, if SHC exists */}
                                        {deploymentType === 'onprem' && results.shcDeployerCount > 0 && (
                                            <Tr>
                                                <Td fontWeight="bold">SHC Deployer</Td>
                                                <Td isNumeric>{results.shcDeployerCount}</Td>
                                                <Td>12 vCPU</Td>
                                                <Td>12 GB</Td>
                                                <Td>200 GB</Td>
                                            </Tr>
                                        )}

                                        {/* Indexers - On-Prem Only */}
                                        {deploymentType === 'onprem' && (
                                            <Tr>
                                                <Td fontWeight="bold">Indexers</Td>
                                                <Td isNumeric>{results.suggestedIndexers}</Td>
                                                <Td>24 vCPU</Td>
                                                <Td>32 GB</Td>
                                                <Td>300 GB + {results.totalPerIdx} TB</Td>
                                            </Tr>
                                        )}

                                        {/* Cluster Master - On-Prem Only */}
                                        {deploymentType === 'onprem' && (
                                            <Tr>
                                                <Td fontWeight="bold">Cluster Master</Td>
                                                <Td isNumeric>{results.clusterMasterCount}</Td>
                                                <Td>12 vCPU</Td>
                                                <Td>16 GB</Td>
                                                <Td>200 GB</Td>
                                            </Tr>
                                        )}

                                        {/* Heavy Forwarders - Common */}
                                        <Tr>
                                            <Td fontWeight="bold">Heavy Forwarders</Td>
                                            <Td isNumeric>{results.suggestedHFs}</Td>
                                            <Td>12 vCPU</Td>
                                            <Td>16 GB</Td>
                                            <Td>200 GB</Td>
                                        </Tr>
                                    </>
                                )}

                                {/* Deployment Servers - Common if > 0 */}
                                {results.suggestedDS > 0 && (
                                    <Tr>
                                        <Td fontWeight="bold">Deployment Servers</Td>
                                        <Td isNumeric>{results.suggestedDS}</Td>
                                        <Td>12 vCPU</Td>
                                        <Td>12 GB</Td>
                                        <Td>200 GB</Td>
                                    </Tr>
                                )}

                                {/* SOAR - Common */}
                                {premiumApps.soar && (
                                    <Tr>
                                        <Td fontWeight="bold">Splunk SOAR Server</Td>
                                        <Td isNumeric>{results.soarServerCount}</Td>
                                        <Td>16 vCPU</Td>
                                        <Td>32 GB</Td>
                                        <Td>500 GB</Td>
                                    </Tr>
                                )}

                                {/* Automation Broker - Common */}
                                {premiumApps.soar && (
                                    <Tr>
                                        <Td fontWeight="bold">Automation Broker</Td>
                                        <Td isNumeric>{results.automationBrokerCount}</Td>
                                        <Td>4 vCPU</Td>
                                        <Td>16 GB</Td>
                                        <Td>100 GB</Td>
                                    </Tr>
                                )}
                            </Tbody>
                        </Table>
                    </TableContainer>
                </CardBody>
            </Card>
        </Stack>
    );
}

export default Body;
